/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useEffect } from 'react';

import { Skeleton } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { Divider1, FlexContainer, VerticallyAlignedFlexContainer } from 'styles/common';

import { InfoIcon2 } from 'constants/icons';
import theme from 'constants/theme';

import {
  getNotificationEventChannelDetails,
  toggleNotificationChannel,
  toggleNotificationChannelForEmployees,
} from 'services/admin';
import { showSuccessSnackbar } from 'services/snackbar';

import AntSwitch from 'uiToolkit/AntSwitch';
import { Typography14, Typography13 } from 'uiToolkit/Typography';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { NOTIFICATION_CHANNELS } from './Constants/NotificationSettingsConstants';
import { useNotificationSettings } from './Context/NotificationSettingsContext';
import { NOTIFICATION_EVENT_CHANNELS } from './NotificationEvent';

const StyledAntSwitch = styled(AntSwitch)`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const ChannelRow = ({ cd, heading, description, img, onToggle, isLastItem }) => {
  const { uuid, enabled, channelType } = cd ?? {};
  return (
    <>
      <VerticallyAlignedFlexContainer style={{ padding: '12px' }}>
        <StyledImage src={img} />
        <FlexContainer style={{ flexDirection: 'column', marginLeft: '1.5rem', marginRight: 'auto' }}>
          <VerticallyAlignedFlexContainer>
            <Typography14 weight={600} customColor={theme.palette.text.blackv1}>
              {heading}
            </Typography14>
            <FlexContainer style={{ marginLeft: '0.5rem' }}>
              {NOTIFICATION_EVENT_CHANNELS[channelType].icon}
            </FlexContainer>
          </VerticallyAlignedFlexContainer>
          <DescriptionContainer>
            <Typography13 customColor={theme.palette.text.blackv1}>{description}</Typography13>
            {channelType === 'EMAIL' && (
              <VerticallyAlignedFlexContainer style={{ marginTop: '0.75rem' }}>
                <InfoIcon2 color={theme.palette.icons.grey4} size="fixed16px" style={{ marginRight: '0.5rem' }} />
                <Typography13 customColor={theme.palette.text.label}>Critical emails are always sent</Typography13>
              </VerticallyAlignedFlexContainer>
            )}
          </DescriptionContainer>
        </FlexContainer>
        <StyledAntSwitch
          checked={enabled}
          onChange={(e) => onToggle({ uuid, channelType, enabled: e.target.checked })}
        />
      </VerticallyAlignedFlexContainer>
      {!isLastItem && <Divider1 />}
    </>
  );
};

const NotificationChannels = ({ isEmployeeNotificationSettings = false }) => {
  const dispatch = useDispatch();
  const {
    state: { channelDetailsArray, loadingChannelDetails },
    setData,
  } = useNotificationSettings();

  const { enabledFeatures = [] } = useSelector(selectLoggedInEmployeeData);

  const fetchData = useCallback(() => {
    getNotificationEventChannelDetails({ isEmployeeNotificationSettings, enabledFeatures }).then((res) =>
      setData({ ...res, loadingChannelDetails: false })
    );
  }, [enabledFeatures, isEmployeeNotificationSettings, setData]);

  useEffect(() => {
    setData({ loadingChannelDetails: true });
    fetchData();
  }, [fetchData, setData]);

  const channelOnToggle = ({ uuid, channelType, enabled }) => {
    if (isEmployeeNotificationSettings) {
      toggleNotificationChannelForEmployees({
        uuid,
        channelType,
        enabled,
        isEmployeeNotificationSettings,
      })
        .then(() => {
          fetchData();
          dispatch(showSuccessSnackbar('Notification settings saved successfully!'));
        })
        .catch((err) => {
          console.error('Error in toggling the channel : ', err);
        });
    } else {
      toggleNotificationChannel({ uuid, channelType, enabled, isEmployeeNotificationSettings })
        .then(() => {
          fetchData();
        })
        .catch((err) => {
          console.error('Error in toggling the channel : ', err);
        });
    }
  };

  if (loadingChannelDetails) {
    return <Skeleton />;
  }

  return (
    <PaperWithShadow>
      {channelDetailsArray.length &&
        channelDetailsArray.map((cd, i) => (
          <ChannelRow
            onToggle={channelOnToggle}
            isEmployeeNotificationSettings={isEmployeeNotificationSettings}
            key={`${cd.uuid}-${cd.channelType}`}
            cd={cd}
            isLastItem={i === channelDetailsArray.length - 1}
            {...NOTIFICATION_CHANNELS[cd.channelType]}
          />
        ))}
    </PaperWithShadow>
  );
};

const StyledImage = styled.img`
  height: 80px;
  width: 80px;
`;

const DescriptionContainer = styled.div`
  max-width: 30rem;
`;

export default NotificationChannels;
